import type { Cart, CartItem } from "@/types"
import type { PriceReview } from "@/types/external"

/**
 * Rewrites the bag with the prices the merchant just published.
 *
 * Runs on BOTH branches of the decision. Accepting means the customer agreed to
 * the new total, so the bag must show it. Declining sends them back to the bag —
 * and a bag still showing the old price would just produce the same divergence
 * on the next attempt.
 *
 * Matching is by catalog item id, the identity the order carries as the line id.
 * A line the review does not mention is left untouched.
 */
export function applyPriceReviewToCart(cart: Cart, review: PriceReview): Cart {
  const byItemId = new Map(review.lines.map((line) => [line.merchantItemId, line]))

  let changed = false
  const items: CartItem[] = cart.items.map((cartItem) => {
    const catalogItemId = cartItem.item?.id || cartItem.itemOffer?.itemId || ""
    const line = byItemId.get(catalogItemId)
    if (!line) return cartItem

    const nextLineTotal = line.currentTotalMinor / 100
    const quantity = cartItem.quantity > 0 ? cartItem.quantity : 1
    const nextUnitPrice = nextLineTotal / quantity

    if (cartItem.itemTotalPrice === nextLineTotal && cartItem.unitPrice === nextUnitPrice) {
      return cartItem
    }

    changed = true
    return { ...cartItem, itemTotalPrice: nextLineTotal, unitPrice: nextUnitPrice }
  })

  if (!changed) return cart

  return {
    ...cart,
    items,
    totalPrice: items.reduce((sum, item) => sum + item.itemTotalPrice, 0),
  }
}
